import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import type { SpatialQueryResult, ParcelWithPrice } from '@/api/mapApi';

interface Props {
  result: SpatialQueryResult;
  location: { lat: number; lng: number };
  onClose: () => void;
}

const LAND_TYPE_NAMES: Record<string, string> = {
  dat_o: 'Đất ở',
  dat_nong_nghiep: 'Đất nông nghiệp',
  dat_cong: 'Đất công',
  dat_san_xuat: 'Đất sản xuất',
  dat_giao_thong: 'Đất giao thông',
  dat_thuy_loi: 'Đất thủy lợi',
  dat_rung: 'Đất rừng',
  dat_khac: 'Khác',
};

const LAND_TYPE_COLORS: Record<string, string> = {
  dat_o: 'bg-red-500',
  dat_nong_nghiep: 'bg-lime-500',
  dat_cong: 'bg-blue-500',
  dat_san_xuat: 'bg-amber-500',
  dat_giao_thong: 'bg-gray-500',
  dat_thuy_loi: 'bg-cyan-500',
  dat_rung: 'bg-green-600',
  dat_khac: 'bg-purple-500',
};

function formatPrice(vnd: number): string {
  if (vnd >= 1_000_000_000) return `${(vnd / 1_000_000_000).toFixed(2)} tỷ`;
  if (vnd >= 1_000_000) return `${(vnd / 1_000_000).toFixed(1)} triệu`;
  return `${vnd.toLocaleString('vi-VN')} đ`;
}

export function QueryResultPanel({ result, location, onClose }: Props) {
  const [tab, setTab] = useState<'projects' | 'parcels'>('projects');
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const isBasicPlus = isAuthenticated && user?.role !== 'super_admin'
    ? user?.role !== undefined  // for now treat all logged-in as basic+
    : false;

  const hasProjects = result.projects.length > 0;
  const hasParcels = result.parcels.length > 0;
  const totalLayers = result.projects.reduce((sum, p) => sum + p.layers.length, 0);

  return (
    <div className="absolute bottom-0 left-0 right-0 md:bottom-4 md:left-4 md:right-auto md:w-96 md:max-h-[80vh] bg-white shadow-2xl rounded-t-2xl md:rounded-lg flex flex-col z-20 max-h-[70vh]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500">Vị trí đã chọn</p>
          <p className="text-sm font-mono text-gray-900 truncate">
            {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
          </p>
        </div>
        <button
          onClick={onClose}
          className="ml-3 text-gray-400 hover:text-gray-600 text-xl leading-none"
          aria-label="Đóng"
        >
          ×
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setTab('projects')}
          className={`flex-1 py-3 text-sm font-medium ${
            tab === 'projects' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-gray-600'
          }`}
        >
          Quy hoạch
          {hasProjects && <span className="ml-1 text-xs text-gray-500">({totalLayers})</span>}
        </button>
        <button
          onClick={() => setTab('parcels')}
          className={`flex-1 py-3 text-sm font-medium ${
            tab === 'parcels' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-gray-600'
          }`}
        >
          Thửa đất
          {hasParcels && <span className="ml-1 text-xs text-gray-500">({result.parcels.length})</span>}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-hidden">
        {tab === 'projects' && <ProjectsTab projects={result.projects} />}
        {tab === 'parcels' && <ParcelsTab parcels={result.parcels} isBasicPlus={isBasicPlus || user?.role === 'super_admin'} />}
      </div>
    </div>
  );
}

function ProjectsTab({ projects }: { projects: SpatialQueryResult['projects'] }) {
  if (projects.length === 0) {
    return <EmptyState message="Không có quy hoạch tại vị trí này" />;
  }
  return (
    <div className="p-3 space-y-3">
      {projects.map((p) => (
        <div key={p.id} className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
            <p className="text-sm font-semibold text-gray-900">{p.name}</p>
          </div>
          <ul className="divide-y divide-gray-100">
            {p.layers.map((l) => (
              <li key={l.id} className="px-3 py-2 flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-sm border border-gray-300"
                  style={{ backgroundColor: l.style.fillColor ?? '#3388ff' }}
                />
                <span className="text-sm text-gray-700 flex-1">{l.name}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ParcelsTab({ parcels, isBasicPlus }: { parcels: ParcelWithPrice[]; isBasicPlus: boolean }) {
  if (parcels.length === 0) {
    return <EmptyState message="Chưa có dữ liệu thửa đất tại vị trí này" />;
  }
  return (
    <div className="p-3 space-y-3">
      {parcels.map((p) => (
        <div key={p.id} className="border border-gray-200 rounded-lg p-3">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {p.parcelCode ?? `Thửa ${p.parcelNumber ?? p.id.slice(0, 6)}`}
              </p>
              {p.sheetNumber && (
                <p className="text-xs text-gray-500">Tờ {p.sheetNumber}</p>
              )}
            </div>
            <span
              className={`inline-block px-2 py-0.5 text-xs text-white rounded ${
                LAND_TYPE_COLORS[p.landType] ?? 'bg-gray-400'
              }`}
            >
              {LAND_TYPE_NAMES[p.landType] ?? p.landType}
            </span>
          </div>

          {p.address && (
            <p className="text-xs text-gray-600 mb-2">📍 {p.address}</p>
          )}

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-gray-500">Diện tích:</span>{' '}
              <span className="font-medium text-gray-900">{p.areaSqm.toFixed(0)} m²</span>
            </div>
            <div>
              <span className="text-gray-500">Loại đất:</span>{' '}
              <span className="font-medium text-gray-900">
                {LAND_TYPE_NAMES[p.landType] ?? p.landType}
              </span>
            </div>
          </div>

          {/* Price - tier gated */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            {p.latestPrice ? (
              isBasicPlus ? (
                <div>
                  <p className="text-xs text-gray-500">Giá giao dịch gần nhất</p>
                  <p className="text-base font-bold text-brand-600">{formatPrice(p.latestPrice)}</p>
                  {p.priceDate && (
                    <p className="text-xs text-gray-400">Ngày {p.priceDate}</p>
                  )}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-2">
                  <p className="text-xs text-amber-800">🔒 Nâng cấp Basic để xem giá giao dịch</p>
                </div>
              )
            ) : (
              <p className="text-xs text-gray-400 italic">Chưa có dữ liệu giá</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="p-8 text-center text-sm text-gray-500">
      <p>{message}</p>
    </div>
  );
}
